import React, { useEffect } from "react";
import { Button, Card, CardBody, Col, Row, Form } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import FormDropzone from "../../shared/dropzone";
import { multipartFormData } from "../../helpers/api.services";
import { PartnerService } from "./service";
import { PARTNER_URL } from "../../helpers/api.routes";
import { ErrorToast, SuccessToast } from "../../shared/toasters/toasters";
import { useTranslation } from "react-i18next";
import { Editor } from "@tinymce/tinymce-react";

const schema = yup.object().shape({
  title: yup.string().required("formErrors.titleRequired"),
  discount: yup.string(),
  link: yup.string(),
  name: yup.string().required("formErrors.titleRequired"),
  text: yup.string(),
  promoCode: yup
    .string()
    .optional()
    .matches(/^[A-Za-z0-9]{1,10}$|^$/, "formErrors.promoCodeInvalid"),
  stripeCouponId: yup.string().optional(),
  picture: yup.mixed(),
});

interface PartnerState {
  title: string;
  discount?: string;
  link?: string;
  name: string;
  text?: string;
  promoCode?: string;
  stripeCouponId?: string;
  pictures?: {
    path?: string;
  };
}

const PartnerForm: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    reset,
    formState: { errors },
  } = useForm<PartnerState>({
    resolver: yupResolver(schema),
    defaultValues: {
      title: "",
      discount: "",
      link: "",
      name: "",
      text: "",
      promoCode: undefined,
      stripeCouponId: "",
      pictures: { path: "" },
    },
  });

  useEffect(() => {
    if (isEdit) {
      const fetchPartner = async () => {
        try {
          const response = await PartnerService.getSingle(+id!);
          reset({
            title: response.title,
            discount: response.discount ?? "",
            link: response.link ?? "",
            name: response.name ?? "",
            text: response.text ?? "",
            promoCode: response.promoCode ?? undefined,
            stripeCouponId: response.stripeCouponId ?? "",
            pictures: response.pictures?.[0],
          });
        } catch (error) {
          ErrorToast(error);
        }
      };
      fetchPartner();
    }
  }, [isEdit, id, reset]);

  const handleFileUpload = (files: any) => {
    setValue("pictures", files[0]);
  };

  const onSubmit = async (data: PartnerState) => {
    const url = isEdit ? `${PARTNER_URL}/${id}` : PARTNER_URL;
    const method = isEdit ? "put" : "post";

    multipartFormData(data.pictures, url, JSON.stringify(data), method, "file")
      .then(() => {
        SuccessToast(
          t(`partner.${isEdit ? "successUpdate" : "successCreate"}`),
        );
        navigate("/partners");
        reset();
      })
      .catch((error) => ErrorToast(error));
  };

  return (
    <React.Fragment>
      <Row>
        <Col sm={12}>
          <Card>
            <CardBody>
              <Form onSubmit={handleSubmit(onSubmit)}>
                <Row className="g-3">
                  <Col sm={6} xl={4}>
                    <div className="mb-0">
                      <label className="form-label">
                        {t("partner.formName")}
                      </label>
                      <Controller
                        name="name"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="text"
                            className={`form-control ${errors.name ? "is-invalid" : ""}`}
                            placeholder={t("partner.enterName")}
                            {...field}
                          />
                        )}
                      />
                      {errors.name && (
                        <div className="invalid-feedback">
                          {t(errors.name.message as string)}
                        </div>
                      )}
                    </div>
                  </Col>
                  <Col sm={6} xl={4}>
                    <div className="mb-0">
                      <label className="form-label">
                        {t("partner.discount")}
                      </label>
                      <Controller
                        name="discount"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="text"
                            className={`form-control ${errors.discount ? "is-invalid" : ""}`}
                            placeholder={t("partner.enterDiscount")}
                            {...field}
                          />
                        )}
                      />
                      {errors.discount && (
                        <div className="invalid-feedback">
                          {t(errors.discount.message as string)}
                        </div>
                      )}
                    </div>
                  </Col>
                  <Col sm={6} xl={4}>
                    <div className="mb-0">
                      <label className="form-label">
                        {t("partner.promoCode")}
                      </label>
                      <Controller
                        name="promoCode"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="text"
                            className={`form-control ${errors.promoCode ? "is-invalid" : ""}`}
                            placeholder={t("partner.enterPromoCode")}
                            maxLength={10}
                            {...field}
                          />
                        )}
                      />
                      {errors.promoCode && (
                        <div className="invalid-feedback">
                          {t(errors.promoCode.message as string)}
                        </div>
                      )}
                    </div>
                  </Col>
                  <Col sm={6} xl={4}>
                    <div className="mb-0">
                      <label className="form-label">
                        {t("partner.stripeCouponId")}
                      </label>
                      <Controller
                        name="stripeCouponId"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="text"
                            className={`form-control ${errors.stripeCouponId ? "is-invalid" : ""}`}
                            placeholder={t("partner.enterStripeCouponId")}
                            {...field}
                          />
                        )}
                      />
                      {errors.stripeCouponId && (
                        <div className="invalid-feedback">
                          {t(errors.stripeCouponId.message as string)}
                        </div>
                      )}
                    </div>
                  </Col>
                  <Col sm={12}>
                    <div className="mb-0">
                      <label className="form-label">
                        {t("partner.link")}
                      </label>
                      <Controller
                        name="link"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="text"
                            className={`form-control ${errors.link ? "is-invalid" : ""}`}
                            placeholder={t("partner.enterLink")}
                            {...field}
                          />
                        )}
                      />
                      {errors.link && (
                        <div className="invalid-feedback">
                          {t(errors.link.message as string)}
                        </div>
                      )}
                    </div>
                  </Col>

                  <Col xs={12}>
                    <hr />
                  </Col>
                  <Col sm={12}>
                    <div className="mb-0">
                      <label className="form-label">
                        {t("partner.title")}
                      </label>
                      <Controller
                        name="title"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="text"
                            className={`form-control ${errors.title ? "is-invalid" : ""}`}
                            placeholder={t("partner.enterTitle")}
                            {...field}
                          />
                        )}
                      />
                      {errors.title && (
                        <div className="invalid-feedback">
                          {t(errors.title.message as string)}
                        </div>
                      )}
                    </div>
                  </Col>
                  <Col sm={12}>
                    <div className="mb-0">
                      <label className="form-label">
                        {t("partner.text")}
                      </label>
                      <Controller
                        name="text"
                        control={control}
                        render={({ field }) => (
                          <Editor
                            apiKey={process.env.REACT_APP_TINYMCE_API_KEY}
                            value={field.value}
                            onEditorChange={(content: string) =>
                              field.onChange(content)
                            }
                            init={{
                              height: 360,
                              menubar: false,
                              branding: false,
                              plugins: [
                                "advlist",
                                "autolink",
                                "lists",
                                "link",
                                "image",
                                "charmap",
                                "preview",
                                "anchor",
                                "searchreplace",
                                "visualblocks",
                                "code",
                                "fullscreen",
                                "insertdatetime",
                                "media",
                                "table",
                                "help",
                                "wordcount",
                              ],
                              toolbar:
                                "undo redo | blocks | bold italic underline strikethrough | " +
                                "alignleft aligncenter alignright alignjustify | " +
                                "bullist numlist outdent indent | table | link image | removeformat | code",
                              content_style:
                                "body { font-family: Helvetica, Arial, sans-serif; font-size: 14px; }",
                            }}
                          />
                        )}
                      />
                      {errors.text && (
                        <div className="invalid-feedback">
                          {t(errors.text.message as string)}
                        </div>
                      )}
                    </div>
                  </Col>
                  <Col sm={3}>
                    <FormDropzone
                      onFileUpload={handleFileUpload}
                      existingFiles={
                        (getValues("pictures") as any)?.path
                          ? [getValues("pictures") as any]
                          : []
                      }
                      accept="image/png,image/jpeg"
                    />
                  </Col>
                  <Col sm={12}>
                    <Button type="submit" variant="primary">
                      {t(`partner.${isEdit ? "update" : "create"}`)}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default PartnerForm;
